/**
 * AK-446 Ш2 — акты списания культуры (`crop_writeoff_acts`, ADR-055 §D4).
 *
 * Контракт на бэкенде существовал с ADR-055 («UI/flow акта — отдельный
 * тикет»), но во фронте не было **ни одного** обращения к `/writeoff-acts`:
 * статус «Списано» был недостижим рукой из карточки поля, а списание —
 * единственный способ закрыть погибший посев (ADR-059 §D8: «смерть посева —
 * только через акт списания»). Этот модуль — недостающая половина.
 *
 * Жизненный цикл документа (держит БД, здесь только вызовы):
 *   draft → confirmed  — проведение, и ТОЛЬКО оно делает занятие «Списано»;
 *   draft|confirmed → cancelled — отмена, статус занятия возвращается к факту.
 * Черновик на карту и карточку не влияет: агроном спокойно готовит документ,
 * поле перекрашивается в момент проведения.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface WriteoffReason {
  id: string;
  code: string;
  name_translations: Record<string, string> | null;
}

export interface WriteoffAct {
  id: string;
  sowing_id: string;
  inspection_id: string | null;
  field_id: string;
  season_id: string | null;
  reason_id: string | null;
  reason_code: string | null;
  reason_name: string | null;
  reason_note: string | null;
  affected_area_ha: number | null;
  photos: Array<Record<string, unknown>>;
  comment: string | null;
  status: 'draft' | 'confirmed' | 'cancelled';
  confirmed_at: string | null;
  created_at: string | null;
  /** derived-статус занятия ПОСЛЕ применения акта — чтобы UI не перезапрашивал карточку. */
  crop_status: string | null;
}

export interface WriteoffActInput {
  sowing_id: string;
  reason_id?: string | null;
  reason_note?: string | null;
  affected_area_ha?: number | null;
  comment?: string | null;
}

export const writeoffKeys = {
  bySowing: (sowingId: string) => ['writeoff-acts', 'sowing', sowingId] as const,
  reasons: ['writeoff-reasons'] as const,
};

/** Причины списания для селекта в акте — справочник `reference.writeoff_reasons`. */
export function useWriteoffReasons(
  enabled = true,
): UseQueryResult<WriteoffReason[], Error> {
  return useQuery<WriteoffReason[], Error>({
    queryKey: writeoffKeys.reasons,
    enabled,
    // Справочник меняется раз в год — держим долго, чтобы диалог открывался
    // мгновенно, а не «толкай и жди».
    staleTime: 30 * 60_000,
    queryFn: async () => {
      const r = await api.get<{ items: WriteoffReason[] }>(
        '/api/v1/reference/writeoff-reasons',
      );
      return r.items ?? [];
    },
  });
}

/** Акты по занятию: активный черновик/проведённый показываем прямо в блоке действий. */
export function useWriteoffActs(
  sowingId: string | null,
): UseQueryResult<WriteoffAct[], Error> {
  return useQuery<WriteoffAct[], Error>({
    queryKey: writeoffKeys.bySowing(sowingId ?? ''),
    enabled: Boolean(sowingId),
    staleTime: 15_000,
    queryFn: async () => {
      const r = await api.get<{ items: WriteoffAct[] }>(
        `/api/v1/writeoff-acts?sowing_id=${encodeURIComponent(sowingId ?? '')}`,
      );
      return r.items ?? [];
    },
  });
}

/** Проведённый неархивный акт — тот, из-за которого занятие «Списано». */
export function activeWriteoffAct(
  acts: WriteoffAct[] | undefined,
): WriteoffAct | null {
  return acts?.find((a) => a.status === 'confirmed') ?? null;
}

/** Незавершённый черновик — его продолжают, а не заводят второй. */
export function draftWriteoffAct(
  acts: WriteoffAct[] | undefined,
): WriteoffAct | null {
  return acts?.find((a) => a.status === 'draft') ?? null;
}

function useInvalidate(sowingId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: writeoffKeys.bySowing(sowingId) });
    // Статус занятия виден в карточке поля и в списке закрытия сезона.
    void qc.invalidateQueries({ queryKey: ['field-card'] });
    void qc.invalidateQueries({ queryKey: ['sowing-status', 'open'] });
  };
}

/** POST /writeoff-acts — черновик. На статус занятия ещё не влияет. */
export function useCreateWriteoffAct(sowingId: string) {
  const invalidate = useInvalidate(sowingId);
  return useMutation<WriteoffAct, Error, WriteoffActInput>({
    mutationFn: (body) => api.post<WriteoffAct>('/api/v1/writeoff-acts', body),
    onSuccess: invalidate,
  });
}

/** POST /writeoff-acts/{id}/confirm — ПРОВЕСТИ: занятие становится «Списано». */
export function useConfirmWriteoffAct(sowingId: string) {
  const invalidate = useInvalidate(sowingId);
  return useMutation<WriteoffAct, Error, { actId: string }>({
    mutationFn: ({ actId }) =>
      api.post<WriteoffAct>(`/api/v1/writeoff-acts/${actId}/confirm`, {}),
    onSuccess: invalidate,
  });
}

/** POST /writeoff-acts/{id}/cancel — отменить (черновик или проведённый). */
export function useCancelWriteoffAct(sowingId: string) {
  const invalidate = useInvalidate(sowingId);
  return useMutation<WriteoffAct, Error, { actId: string }>({
    mutationFn: ({ actId }) =>
      api.post<WriteoffAct>(`/api/v1/writeoff-acts/${actId}/cancel`, {}),
    onSuccess: invalidate,
  });
}

/** Подпись причины на языке агронома. */
export function writeoffReasonLabel(r: WriteoffReason): string {
  return r.name_translations?.ru ?? r.code;
}
